import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  Home as HomeIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { clientsAPI, projectsAPI, tasksAPI } from '../services/api';

export default function Projects() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    status: 'развитие с нуля'
  });

  const statuses = [
    { value: 'развитие с нуля', label: 'развитие с нуля', color: '#4CAF50', group: 'green' },
    { value: 'развитие покупают', label: 'развитие покупают', color: '#4CAF50', group: 'green' },
    { value: 'регулярный успешный', label: 'регулярный успешный', color: '#4CAF50', group: 'green' },
    { value: 'угасает', label: 'угасает', color: '#FF9800', group: 'yellow' },
    { value: 'закрыт', label: 'закрыт', color: '#F44336', group: 'red' }
  ];

  const getStatusMeta = (statusValue) => {
    const found = statuses.find(s => s.value === statusValue);
    if (found) return found;
    if (!statusValue) return { label: '-', color: '#9e9e9e', group: 'unknown' };
    return { label: String(statusValue), color: '#9e9e9e', group: 'unknown' };
  };

  const statusGroupOrder = {
    green: 0,
    yellow: 1,
    red: 2,
    unknown: 3
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [projectsRes, tasksRes, clientsRes] = await Promise.all([
        projectsAPI.getAll(),
        tasksAPI.getAll(),
        clientsAPI.getAll()
      ]);
      setProjects(projectsRes.data);
      setTasks(tasksRes.data);
      setClients(clientsRes.data);
    } catch (err) {
      setError('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (project = null) => {
    if (project) {
      setEditingProject(project);
      setFormData({
        name: project.name,
        status: project.status || statuses[0]?.value || 'развитие с нуля'
      });
    } else {
      setEditingProject(null);
      setFormData({
        name: '',
        status: statuses[0]?.value || 'развитие с нуля'
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingProject(null);
  };

  const handleSave = async () => {
    try {
      if (editingProject) {
        await projectsAPI.update(editingProject.id, formData);
      } else {
        await projectsAPI.create(formData);
      }
      await loadData();
      handleCloseDialog();
    } catch (err) {
      setError('Ошибка сохранения');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Удалить проект?')) {
      try {
        await projectsAPI.delete(id);
        await loadData();
      } catch (err) {
        setError('Ошибка удаления');
      }
    }
  };

  // Получить количество клиентов с задачами по проекту
  const getClientCount = (projectId) => {
    const projectTasks = tasks.filter(t => t.projectId === projectId);
    const uniqueClients = new Set();
    projectTasks.forEach(task => {
      if (task.clientId) {
        uniqueClients.add(task.clientId);
      }
    });
    return uniqueClients.size;
  };

  // Получить клиентов с задачами по проекту
  const getProjectClients = (projectId) => {
    const projectTasks = tasks.filter(t => t.projectId === projectId);
    const clientMap = new Map();

    projectTasks.forEach(task => {
      if (task.clientId) {
        const clientId = task.clientId;
        if (!clientMap.has(clientId)) {
          const client = clients.find(c => c.id === clientId) || task.client;
          if (client) {
            clientMap.set(clientId, {
              ...client,
              taskCount: 0
            });
          }
        }
        if (clientMap.has(clientId)) {
          clientMap.get(clientId).taskCount++;
        }
      }
    });

    return Array.from(clientMap.values());
  };

  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.name.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || project.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const sortedProjects = [...filteredProjects].sort((a, b) => {
    if (statusFilter !== 'all') {
      return a.name.localeCompare(b.name, 'ru');
    }

    const aMeta = getStatusMeta(a.status);
    const bMeta = getStatusMeta(b.status);
    const aGroup = statusGroupOrder[aMeta.group] ?? statusGroupOrder.unknown;
    const bGroup = statusGroupOrder[bMeta.group] ?? statusGroupOrder.unknown;

    if (aGroup !== bGroup) return aGroup - bGroup;
    return a.name.localeCompare(b.name, 'ru');
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={1}>
        <Typography variant={isMobile ? "h6" : "h4"} fontWeight="bold">
          Проекты
        </Typography>
        <Box display="flex" gap={1}>
          <Button
            startIcon={<HomeIcon />}
            onClick={() => navigate('/')}
            size="small"
            variant="outlined"
          >
            {!isMobile && 'Главная'}
          </Button>
          <Button
            variant="contained"
            onClick={() => handleOpenDialog()}
            size="small"
          >
            {isMobile ? <AddIcon /> : <>
              <AddIcon sx={{ mr: 1 }} />
              Добавить
            </>}
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
          <Box display="flex" flexDirection="column" gap={1.5}>
            <TextField
              placeholder="Поиск по названию..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
              fullWidth
              size="small"
            />
            <TextField
              select
              label="Статус"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              fullWidth
              size="small"
            >
              <MenuItem value="all">Все</MenuItem>
              {statuses.map(s => (
                <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
              ))}
            </TextField>
          </Box>
        </CardContent>
      </Card>

      <Box display="flex" flexDirection="column" gap={1.5}>
        {sortedProjects.map((project) => {
          const projectClients = getProjectClients(project.id);
          const clientCount = projectClients.length;

          return (
            <Accordion key={project.id}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" pr={2}>
                  <Box flex={1}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {project.name}
                    </Typography>
                    <Box display="flex" gap={1} mt={0.5} flexWrap="wrap">
                      <Chip
                        label={getStatusMeta(project.status).label}
                        size="small"
                        sx={{
                          bgcolor: getStatusMeta(project.status).color,
                          color: 'white',
                          height: 20,
                          fontSize: '0.75rem'
                        }}
                      />
                      <Chip
                        label={`${clientCount} ${clientCount === 1 ? 'клиент' : 'клиентов'}`}
                        size="small"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.75rem' }}
                      />
                    </Box>
                  </Box>
                  <Box display="flex" gap={0.5} onClick={(e) => e.stopPropagation()}>
                    <IconButton size="small" onClick={() => handleOpenDialog(project)} sx={{ p: 0.5 }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(project.id)} sx={{ p: 0.5 }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {projectClients.length > 0 ? (
                  <List dense>
                    {projectClients.map((client) => (
                      <ListItem
                        key={client.id}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                        onClick={() => navigate(`/clients/${client.id}`)}
                      >
                        <ListItemText
                          primary={client.companyName}
                          secondary={`Задач: ${client.taskCount}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography color="text.secondary" variant="body2">
                    Нет клиентов с задачами по этому проекту
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>
          );
        })}
        {sortedProjects.length === 0 && (
          <Card>
            <CardContent>
              <Typography color="text.secondary" align="center">
                Нет проектов
              </Typography>
            </CardContent>
          </Card>
        )}
      </Box>

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          {editingProject ? 'Редактировать проект' : 'Добавить проект'}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Название проекта"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              fullWidth
              size="small"
            />
            <TextField
              select
              label="Статус"
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              fullWidth
              size="small"
            >
              {statuses.map(s => (
                <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Отмена</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!formData.name}
          >
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
